import { useCallback } from 'react';
import { MessageInstance } from 'antd/es/message/interface';
import { useTranslation } from 'react-i18next';

const defaultErrorMessage =
  "Couldn't copy. Please select the text and use Ctrl+C (Tap & hold on phone) to copy it manually.";

const useCopyToClipboard = (message: MessageInstance) => {
  const { t } = useTranslation('Vindoc');
  const handleTextCopy = useCallback(
    (text: string, messages?: Messages) => {
      try {
        navigator.clipboard.writeText(text);
        message.success(messages?.success || t('Copied to clipboard.'));
      } catch (e) {
        console.log(`📕 e - 10:useCopyToClipboard.ts \n`, e);
        message.error(messages?.error || t(defaultErrorMessage));
      }
    },
    [message, t]
  );

  const handleImageCopy = useCallback(
    (blob: Blob | string, messages?: Messages) => {
      try {
        navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
        message.success(messages?.success || t('QR Code copied to clipboard.'));
      } catch (e) {
        console.log(`📕 e - 29:useCopyToClipboard.ts \n`, e);
        message.error(messages?.error || t("Couldn't generate QR Code."));
      }
    },
    [message, t]
  );

  return useCallback(
    (
      content: Blob | string,
      config: {
        type?: 'text' | 'image';
        messages?: Messages;
      } = {
        type: 'text',
      }
    ) => {
      if (!content) {
        return message.error(config?.messages?.error || t(defaultErrorMessage));
      }

      if (config?.type === 'image')
        return handleImageCopy(content, config?.messages);
      return handleTextCopy(content as string, config?.messages);
    },
    [handleImageCopy, handleTextCopy, message, t]
  );
};

export default useCopyToClipboard;

interface Messages {
  success?: string;
  error?: string;
}
